import { useIsUserRole, useUserData } from "@/app/utils/auth";
import * as Sentry from "@sentry/react";
import { useAuth } from "react-oidc-context";
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
  useParams,
} from "react-router";
import LoadingSpinnerComponent from "./components/LoadingSpinnerComponent";
import ChatPage from "./pages/chatpage/ChatPage";
import CourseEnrollmentPage from "./pages/courseEnrollmentPage/CourseEnrollmentPage";
import { CourseManagementPage } from "./pages/courseManagementPage/courseManagementPage";
import CoursePage from "./pages/coursepage/CoursePage";
import DashboardPage from "./pages/dashboardPage/DashboardPage";
import EditAndGenerateQuestionPage from "./pages/editAndGenerateQuestionPage/editAndGenerateQuestionPage";
import Homepage from "./pages/homepage/HomePage";
import ImprintPage from "./pages/imprintpage/ImprintPage";
import LoginPage from "./pages/loginpage/LoginPage";
import NotificationManagementPage from "./pages/notificationManagement/NotificationManagementPage";
import PrivacyConsentPage from "./pages/privacyConsentPage/PrivacyConsentPage";
import PrivacyPage from "./pages/privacypage/PrivacyPage";
import QuizPage from "./pages/quizpage/QuizPage";
import QuizSessionPage from "./pages/quizpage/QuizSessionPage";
import SurveyPage from "./pages/surveyPage/SurveyPage";
import UserManagementPage from "./pages/user-management/UserManagementPage";
import UserProfilePage from "./pages/userProfilePage/UserProfilePage";
import { useGetCourseQuery } from "./services/courseApi";
import { useGetSurveyQuery } from "./services/surveyApi";
import { buildQueryString } from "./utils/api.util";

const RequireAuth = ({ children }: { children: JSX.Element }): JSX.Element => {
  const auth = useAuth();
  const { isLoading, user } = useUserData();
  const location = useLocation();

  if (auth.isLoading || isLoading) {
    return <LoadingSpinnerComponent />;
  }

  if (!auth.isAuthenticated) {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        to={buildQueryString("login", { redirect: currentPath })}
        replace
      />
    );
  }

  Sentry.setUser(user ? { id: user.id } : null);
  Sentry.setTag("user-role", user?.role);

  return children;
};

const RequirePrivacyConsent = ({ children }: { children: JSX.Element }) => {
  const { privacyConsentRequired } = useUserData();

  if (privacyConsentRequired) {
    return <Navigate to="/privacyConsent" replace />;
  }

  return children;
};

const RequireSurvey = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const { courseId } = useParams();
  if (!courseId) {
    return <Navigate to="/" replace />;
  }
  const isOnSurveyPage = location.pathname.endsWith("/survey");
  if (isOnSurveyPage) {
    return children;
  }

  const { data: survey } = useGetSurveyQuery(courseId);
  if (survey) {
    return <Navigate to={`/course/${courseId}/survey`} replace />;
  }

  return children;
};

const RequireLecturer = ({
  children,
}: { children: JSX.Element }): JSX.Element => {
  if (!useIsUserRole("lecturer", "admin")) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const RequireAdmin = ({ children }: { children: JSX.Element }): JSX.Element => {
  if (!useIsUserRole("admin")) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const RequireManager = ({
  children,
}: { children: JSX.Element }): JSX.Element => {
  const { user } = useUserData();
  const { courseId } = useParams();

  if (!user || !courseId) {
    return <Navigate to="/" replace />;
  }

  const { data: course, isLoading } = useGetCourseQuery(courseId);

  if (isLoading) {
    return <LoadingSpinnerComponent />;
  }

  const isManaging = course?.isManaging || user.role === "admin";

  if (!isManaging) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
  },
  {
    path: "/imprint",
    element: <ImprintPage />,
  },
  {
    path: "/privacyConsent",
    element: <PrivacyConsentPage />,
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <RequirePrivacyConsent>
          <Outlet />
        </RequirePrivacyConsent>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Homepage /> },
      {
        path: "/profile",
        element: <UserProfilePage />,
      },
      {
        path: "course/:courseId/*",
        element: (
          <RequireSurvey>
            <Outlet />
          </RequireSurvey>
        ),
        children: [
          { index: true, element: <CoursePage /> },
          { path: "chat", element: <ChatPage /> },
          { path: "quiz", element: <QuizPage /> },
          { path: "quiz/session", element: <QuizSessionPage /> },
          { path: "enroll", element: <CourseEnrollmentPage /> },
          {
            path: "survey",
            element: <SurveyPage />,
          },
          {
            path: "manage",
            element: (
              <RequireManager>
                <Outlet />
              </RequireManager>
            ),
            children: [
              { index: true, element: <CourseManagementPage /> },
              { path: "quiz", element: <EditAndGenerateQuestionPage /> },
              { path: "dashboard", element: <DashboardPage /> },
            ],
          },
        ],
      },
      {
        path: "course/create",
        element: (
          <RequireLecturer>
            <CourseManagementPage />
          </RequireLecturer>
        ),
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <RequireAuth>
        <RequireAdmin>
          <Outlet />
        </RequireAdmin>
      </RequireAuth>
    ),
    children: [
      { path: "notifications", element: <NotificationManagementPage /> },
      { path: "users", element: <UserManagementPage /> },
    ],
  },
  { path: "*", element: <Navigate to="/" /> },
]);
